import { Loader2 } from 'lucide-react';
import { STRINGS } from '@/strings/strings';

/**
 * v3 §5 Bausteine (line 218) — Kachel + Reihe primitive.
 *
 * Sebastian ruling 2026-08-12 (preview-strip wave) — three-state page
 * tiles; fourth state 'blocked' added 2026-08-26. Each page tile
 * carries one of:
 *   - 'rendering' — spinner + `STRINGS.book.preview.tileRendering`
 *   - 'ready'     — the image + the pageNo badge
 *   - 'failed'    — static placeholder + `STRINGS.book.preview.tileFailed`
 *                   No spinner. Sebastian: "motion belongs on the
 *                   rendering state ... the failed state stays static
 *                   — there nothing is happening, and a spinner would lie."
 *   - 'blocked'   — static placeholder + `STRINGS.book.preview.tileBlocked`
 *                   verbatim. Fires when the backend detects the scene
 *                   has no assembled_prompt during preview_rendering
 *                   (an upstream defect the customer cannot self-heal).
 *                   No spinner, no CTA, no link. Same visual as failed
 *                   (dashed error border, error-toned copy) — different
 *                   text because the customer's next action is to reach
 *                   out to us, not just move on.
 *
 * Verbatim from v3 §5:
 *   Reihe: `flex gap-3 overflow-x-auto md:overflow-visible
 *           md:justify-center`
 *   Kachel: `h-[126px] w-[126px] shrink-0 rounded-lg`, active
 *           `ring-2 ring-dream-gold`
 *
 * v3 §3 "Rot folgt dem Knopf" preserved: the failed and blocked tiles
 * carry a dashed error border because the customer's next action is
 * to move on (failed) or reach out to us (blocked). Rendering tiles
 * use the neutral navy-tint border because there is nothing to do but
 * wait.
 *
 * Sebastian ruling 2026-08-12: page-tile label reads
 * `Szene {szeneLetter} · im Buch Seite {pageNo}` — same wording as
 * the big-view position label.
 */

interface CoverTileEntry {
  readonly kind: 'cover';
  readonly imageUrl: string | null;
}

interface PageTileEntry {
  readonly kind: 'page';
  readonly pageNo: number;
  readonly szeneLetter: string;
  readonly state: 'rendering' | 'ready' | 'failed' | 'blocked';
  /**
   * 2026-08-22 wiring commit — mirror the envelope rename.
   * Screen-composed page image (text baked in). Non-null iff
   * `state === 'ready'`. Kept as the fallback source when
   * `rawImageUrl` is null on a 'ready' tile (raw-URL sign failed
   * server-side; the composed image is still valid and better than
   * a placeholder).
   */
  readonly composedImageUrl: string | null;
  /**
   * Sebastian ruling 2026-09-01 — the RAW picked scene image (no
   * text panel). The four preview tiles render this by preference:
   * the customer sees "how the figures look" without the printed-
   * page text panel obscuring the illustration. Fallback to
   * `composedImageUrl` when null on a 'ready' tile. Both come from
   * the SAME underlying RenderAttempt (backend
   * `SceneComposedImageService.pickedRawImageLocation` uses the
   * same picker as the compose call), so there is no risk of the
   * fallback showing an image from a different attempt.
   */
  readonly rawImageUrl: string | null;
}

type TileEntry = CoverTileEntry | PageTileEntry;

export interface TileRowProps {
  readonly tiles: readonly TileEntry[];
  /** Index of the tile currently shown in the big view. */
  readonly activeIndex: number;
  readonly onSelect: (index: number) => void;
}

export function TileRow({ tiles, activeIndex, onSelect }: TileRowProps) {
  return (
    <div
      role="tablist"
      aria-label={STRINGS.book.preview.thumbRowAriaLabel}
      className="flex gap-3 overflow-x-auto md:overflow-visible md:justify-center pb-2"
    >
      {tiles.map((tile, index) => {
        const isActive = index === activeIndex;
        // Block 5 · 5.5 (Sebastian ruling 2026-09-09) — captions now
        // render visibly below each tile. Format:
        //   anchor  → "Eure Figuren"
        //   pages   → "Seite {pageNo} im Buch"  (page number follows
        //             the actual scene index — 1/7/13/19 for a
        //             standard 4-of-20 distribution). `szeneLetter`
        //             (A/B/C/D) is retired from the customer surface.
        const caption =
          tile.kind === 'cover'
            ? STRINGS.book.preview.thumbCoverLabel
            : STRINGS.book.preview.thumbPageLabel.replace('{pageNo}', tile.pageNo.toString());
        const errorBorder =
          tile.kind === 'page' && (tile.state === 'failed' || tile.state === 'blocked');
        return (
          <div
            key={tile.kind === 'cover' ? 'cover' : `page-${tile.pageNo}`}
            className="flex flex-col items-center gap-2"
          >
            <button
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-label={caption}
              onClick={() => onSelect(index)}
              className={`relative h-[126px] w-[126px] shrink-0 overflow-hidden rounded-lg bg-dream-white transition-colors focus-visible:outline-2 focus-visible:outline-dream-navy/50 ${
                isActive ? 'ring-2 ring-dream-gold' : ''
              } ${errorBorder ? 'border border-dashed border-error' : 'border border-dream-navy/15'}`}
            >
              {tile.kind === 'cover' ? (
                tile.imageUrl && (
                  <img
                    src={tile.imageUrl}
                    alt=""
                    aria-hidden
                    className="h-full w-full object-cover"
                    // Sebastian ruling 2026-09-24 — thumb strip
                    // scrolls horizontally; only the active +
                    // adjacent tiles are on-screen on mount. Lazy
                    // loading defers the off-screen tile fetches
                    // until they scroll into view (or, for the
                    // vertical strip in ReviewScreen, until the
                    // customer scrolls). Cuts cold-load traffic
                    // ~90 % on the finished-book path.
                    loading="lazy"
                    decoding="async"
                  />
                )
              ) : tile.state === 'ready' && (tile.rawImageUrl || tile.composedImageUrl) ? (
                <img
                  src={tile.rawImageUrl ?? tile.composedImageUrl!}
                  alt=""
                  aria-hidden
                  className="h-full w-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              ) : tile.state === 'rendering' ? (
                <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-2 text-center">
                  <Loader2 aria-hidden className="h-6 w-6 animate-spin text-dream-navy/60" />
                  <span className="text-xxs leading-tight text-dream-navy/70">
                    {STRINGS.book.preview.tileRendering}
                  </span>
                </div>
              ) : tile.state === 'blocked' ? (
                <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-2 text-center">
                  <span className="text-xxs leading-tight text-error">
                    {STRINGS.book.preview.tileBlocked}
                  </span>
                </div>
              ) : (
                <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-2 text-center">
                  <span className="text-xxs leading-tight text-error">
                    {STRINGS.book.preview.tileFailed}
                  </span>
                </div>
              )}
            </button>
            <p aria-hidden className="text-xs font-medium text-book-text-secondary text-center">
              {caption}
            </p>
          </div>
        );
      })}
    </div>
  );
}
